import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { getButtonStyles } from '@sc/shared-ui';
import env from '../environment';
import type { FeaturePageDataInterface } from '../assets/data/features';
import Breadcrumbs from './Breadcrumbs';
import { BottomCTA } from './BottomCTA';
import { ContentBlockRenderer } from './ContentBlockRenderer';
import { ResponsiveImage } from './ResponsiveImage';

const FEATURE_IMAGE_SIZES = '(max-width: 888px) 100vw, 848px';

export const featureSeo = (data: FeaturePageDataInterface) => ({
  ...data.seo,
  preloadImage: { src: data.image.src, sizes: FEATURE_IMAGE_SIZES },
});

export function FeatureArticle({ data }: { data: FeaturePageDataInterface }) {
  return (
    <article className="overflow-x-hidden">
      {/* ─── Header ──────────────────────────────────────────────────────── */}
      <section className="pt-15 lg:pt-20 pb-10 px-5">
        <div className="max-w-160 mx-auto text-center">
          <Breadcrumbs
            center
            items={[{ label: 'Home', path: '/' }, { label: data.navTitle }]}
          />
          <h1 className="font-grotesque text-[2.25rem] leading-[1.15] lg:text-[3.5rem] lg:leading-[1.1] font-medium text-white mb-5">
            {data.headline}{' '}
            <span className="text-primary-400">{data.highlight}</span>
          </h1>
          <p className="text-lg leading-7 text-grey-300 max-w-160 mx-auto">
            {data.blurb}
          </p>
        </div>
      </section>

      {/* ─── CTAs ────────────────────────────────────────────────────────── */}
      <section className="pb-15 px-5">
        <div className="flex items-center gap-3 flex-wrap justify-center">
          <a
            href={env.appUrl + '/register'}
            target="_blank"
            rel="noreferrer noopener"
            aria-label="Try Stark Crawl for free (opens in new tab)"
            {...getButtonStyles({
              type: 'primary',
              size: 'xl',
              additionalClassnames: 'gap-x-2',
            })}
          >
            Try for free
            <ArrowRightIcon className="w-4.5 h-4.5" aria-hidden="true" />
          </a>
        </div>
      </section>

      {/* ─── Image ───────────────────────────────────────────────────────── */}
      <section className="pb-15 px-5">
        <div className="max-w-212 mx-auto">
          <div className="relative flex flex-col items-center rounded-lg overflow-hidden border-8 border-grey-t-700">
            <ResponsiveImage
              src={data.image.src}
              alt={data.image.alt}
              className="w-full max-h-120 object-cover rounded-lg"
              sizes={FEATURE_IMAGE_SIZES}
              fetchPriority="high"
            />
          </div>
        </div>
      </section>

      {/* ─── Content ─────────────────────────────────────────────────────── */}
      <section className="pb-20 px-5">
        <div className="max-w-160 mx-auto">
          {data.content.map((block, idx) => (
            <ContentBlockRenderer key={idx} block={block} />
          ))}
        </div>
      </section>

      {/* ─── Bottom CTA ──────────────────────────────────────────────────── */}
      <BottomCTA title={data.ctaTitle} body={data.ctaBody} />
    </article>
  );
}
